// Domain contracts for the constellation tool. Pure TypeScript — no React,
// no transport. Enums mirror the backend
// (backend/app/domain/models/constellation_board.py). Keep in sync.

export enum FaceExpression {
    NEUTRAL = "neutral",
    HAPPY = "happy",
    SAD = "sad",
    ANGRY = "angry",
    SURPRISED = "surprised",
    FEARFUL = "fearful",
    GRATITUDE = "gratitude",
    PAIN = "pain",
    TRUST = "trust",
    LOVE = "love",
    SHAME = "shame",
    FEAR = "fear",
    RAGE = "rage",
    ANGER = "anger",
    GUILT = "guilt",
    SADNESS = "sadness",
    ANXIETY = "anxiety",
    JOY = "joy",
}

export enum ComplexityLevel {
    SIMPLE = "simple",
    DETAILED = "detailed",
}

export enum ClothesStyle {
    CASUAL = "casual",
    FORMAL = "formal",
    SPORT = "sport",
    NEUTRAL = "neutral",
}

// 20 ready-to-place figures. Keep in sync with backend FigurePreset.
export enum FigurePreset {
    GIRL_LIGHT = "girlLight",
    GIRL_MEDIUM = "girlMedium",
    GIRL_DARK = "girlDark",
    BOY_LIGHT = "boyLight",
    BOY_MEDIUM = "boyMedium",
    BOY_DARK = "boyDark",
    WOMAN_TALL = "womanTall",
    WOMAN_BLONDE = "womanBlonde",
    MAN_TALL = "manTall",
    MAN_REDHEAD = "manRedhead",
    GRANDFATHER = "grandfather",
    GRANDFATHER_GREY = "grandfatherGrey",
    GRANDMOTHER = "grandmother",
    GRANDMOTHER_GREY = "grandmotherGrey",
    MONSTER_GREEN = "monsterGreen",
    MONSTER_RED = "monsterRed",
    MONSTER_PURPLE = "monsterPurple",
    GHOST = "ghost",
    BLOB = "blob",
    HOUSE = "house",
    TREE = "tree",
    STONE = "stone",
    CHAIR = "chair",
    MONEY = "money",
    WALL = "wall",
    WOOD_ADULT = "woodAdult",
    PREGNANT = "pregnant",
    HELPER = "helper",
    AUTHORITY = "authority",
    ANCESTOR = "ancestor",
    EXCLUDED = "excluded",
    DOOR = "door",
    BRIDGE = "bridge",
    BED = "bed",
    BOOK = "book",
    CLOCK = "clock",
    // Role figures
    DOCTOR_WOMAN = "doctorWoman",
    DOCTOR_MAN = "doctorMan",
    POLICE_MAN = "policeMan",
    POLICE_WOMAN = "policeWoman",
    WITCH_DARK = "witchDark",
    WITCH_RED = "witchRed",
    DEAD_MAN = "deadMan",
    DEAD_WOMAN = "deadWoman",
    FAIRY_PINK = "fairyPink",
    FAIRY_BLONDE = "fairyBlonde",
    OGRE = "ogre",
    PRINCESS_BLONDE = "princessBlonde",
    PRINCESS_DARK = "princessDark",
    AVATAR = "avatar",
}

export const DEFAULT_FIGURE = FigurePreset.GIRL_LIGHT;

// Live pose/action for a figure. Keep in sync with backend FigureAction.
export enum FigureAction {
    STAND = "stand",
    DEAD = "dead",
}

export const STATE_SCHEMA_VERSION = 2;

// Who owns a figure. Mirrors backend FigureOwnerType.
export enum FigureOwnerType {
    THERAPIST = "therapist",
    PARTICIPANT = "participant",
}

export interface Vector3 {
    x: number;
    y: number;
    z: number;
}

// Per-category modular-avatar selection ({assetId, color}); kept loose here to
// avoid a domain→avatar module import cycle.
export type AvatarConfig = Record<string, { assetId: string | null; color: string }>;

// A reusable avatar figure created by a therapist and persisted in the DB.
// Selectable on the board by the therapist (managed) and their patients (read).
export interface TherapistFigure {
    id: string;
    name: string;
    avatar: AvatarConfig;
    thumbnail: string | null;
    // World height for the placed figure (defaults applied if absent).
    height?: number | null;
    created_at?: string;
    updated_at?: string;
}

export interface Puppet {
    id: string;
    name: string;
    figure: FigurePreset;
    face: FaceExpression;
    complexity: ComplexityLevel;
    clothes: ClothesStyle;
    note?: string;
    action?: FigureAction;
    position: Vector3;
    rotationY: number;
    avatar?: AvatarConfig;
    // Optional world-height override (used by created avatar figures).
    height?: number;
    // Ownership attribution (state schema v2). ownerId is the participant id
    // for participant-owned figures, null for therapist-owned. ownerName is a
    // denormalised display label — never an authorization value.
    ownerId?: string | null;
    ownerType?: FigureOwnerType;
    ownerName?: string;
}

export interface BoardTable {
    shape: "square" | "circle";
    size: number;
    color?: string;
}

// Free-floating text note placed anywhere on the board (not tied to a figure).
export interface BoardNote {
    id: string;
    text: string;
    position: Vector3;
}

// Floor delimitation area (e.g. past / present / future).
export interface BoardZone {
    id: string;
    label: string;
    position: Vector3;
    radius: number; // circle radius
    width: number; // square full width (x)
    depth: number; // square full depth (z)
    color: string;
    shape: "circle" | "square";
}

export type LinkWeight = "low" | "medium" | "big";

// Mirrors MAX_LINK_LABEL in the backend board domain model. Boards that do not
// receive the server limits (the therapist view) bound the input with this, so
// a too-long label is never rejected on save.
export const MAX_LINK_LABEL_LENGTH = 40;

// Palette offered for links in the link editor.
export const LINK_COLORS = ["#334155", "#ef4444", "#3b82f6", "#22c55e", "#f59e0b", "#a855f7"];

// A connection line between two figures.
export interface BoardLink {
    id: string;
    source: string; // puppet id
    target: string; // puppet id
    color: string;
    weight: LinkWeight;
    label: string;
}

export type Ambiance = "base" | "day" | "night" | "rainy" | "bamboo";

export interface BoardState {
    schemaVersion: number;
    title?: string;
    ambiance?: Ambiance;
    table: BoardTable;
    puppets: Puppet[];
    notes: BoardNote[];
    zones: BoardZone[];
    links: BoardLink[];
}

export type BoardStatus = "draft" | "active" | "closed";

export type SessionType = "individual" | "couple" | "group" | "anonymous";

// Couple, group and anonymous boards have multiple concurrent writers, so they
// use last-write-wins (no optimistic version check); individual boards keep the
// optimistic lock. Single source of truth for that consistency policy.
export function isMultiWriterBoard(t: SessionType | null): boolean {
    return t === "couple" || t === "group" || t === "anonymous";
}

/** Boards joined through one shared link, where the therapist may remove somebody. */
export function isOpenToMany(t: SessionType | null): boolean {
    return t === "group" || t === "anonymous";
}

export interface ParticipantPresence {
    display_name: string;
}

export interface TherapistParticipantPresence {
    participant_id: string;
    display_name: string;
}

export interface BoardSnapshot {
    url: string;
    name: string;
}

export interface ConstellationBoard {
    id: string;
    session_id: string;
    status: BoardStatus;
    state: BoardState;
    version: number;
    snapshots: BoardSnapshot[];
    session_type: SessionType;
    participants: TherapistParticipantPresence[];
}

export interface LaunchResponse {
    board: ConstellationBoard;
    session_id: string;
}

export interface ShareLink {
    token: string;
    url: string;
    expires_at: string;
}

export interface ConstellationSessionItem {
    board_id: string;
    session_id: string;
    title: string;
    date: string;
    session_type: SessionType;
    status: BoardStatus;
    patient_id: string | null;
    couple_patient_id: string | null;
    group_patient_id?: string | null;
    subject_name: string | null;
}

export interface ConstellationSessionsPage {
    sessions: ConstellationSessionItem[];
    total: number;
}

// A reusable, therapist-owned copy of a board state (issue #197).
export interface ConstellationScene {
    id: string;
    name: string;
    description: string;
    patient_id?: string | null;
    couple_patient_id?: string | null;
    is_template: boolean;
    state: BoardState;
    created_at?: string | null;
    updated_at?: string | null;
    /** In the centre's shared library rather than the caller's own. */
    shared_with_center?: boolean;
    is_own?: boolean;
    /** The author always; a centre administrator for the centre's scenes. */
    can_edit?: boolean;
    /** Who saved a colleague's centre scene. */
    author_name?: string | null;
}

export interface ConstellationScenesPage {
    items: ConstellationScene[];
    total: number;
}

export interface ListConstellationSessionsParams {
    from_date?: string;
    to_date?: string;
    session_type?: SessionType;
    patient_id?: string;
    couple_patient_id?: string;
    group_patient_id?: string;
    offset?: number;
    limit?: number;
}

// Patient-facing view: board state plus the allowed attribute catalogues.
export interface PublicBoard {
    state: BoardState;
    version: number;
    allowed_figures: FigurePreset[];
    allowed_actions: FigureAction[];
    allowed_faces: FaceExpression[];
    allowed_complexities: ComplexityLevel[];
    allowed_clothes: ClothesStyle[];
    max_puppets: number;
    max_name_length: number;
    max_notes: number;
    max_note_length: number;
    max_zones: number;
    max_zone_label: number;
    min_zone_radius: number;
    max_zone_radius: number;
    max_links: number;
    max_link_label: number;
    link_weights: LinkWeight[];
    ambiances: Ambiance[];
    session_type: SessionType;
    participants: ParticipantPresence[];
}

export interface JoinResponse {
    participant_id: string;
    board: PublicBoard;
}

export function emptyBoardState(): BoardState {
    return {
        schemaVersion: STATE_SCHEMA_VERSION,
        title: "",
        ambiance: "base",
        table: { shape: "circle", size: 1.0, color: "#dcf5f4" },
        puppets: [],
        notes: [],
        zones: [],
        links: [],
    };
}
